/** The year-on-year bridge (moved out of `Reports.tsx` unchanged; it is the Bridge tab now). */

import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useImportedFigures } from "../api/imported";
import type { Scope2Method } from "../api/queries";
import { useReportComparison } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { WindowLockBanner } from "../components/WindowLockBanner";
import { categoryLabel } from "../lib/categoryLabel";
import { quantity, shortDate } from "../lib/format";
import { BASE_PLACES, bridgeCells, sitesBehind, statedBasisCells } from "./bridgeModel";
import type { SitesTotal } from "./bridgeModel";
import styles from "./Screen.module.css";
import own from "./Reports.module.css";
import { cls } from "./reportsFormat";

/** Why this total differs from last year's — **ACC-10**.
 *
 * A company that sold a site last March reports a lower figure, the figure is correct, and it looks
 * exactly like a company that reduced its emissions. The first person to notice the difference
 * should not be an assurance provider.
 *
 * Three things this panel has to hold:
 *
 * - **Explained and unexplained are separate numbers.** A single "change against last year" is the
 *   figure that hides a divestment inside an improvement.
 * - **An unexplained movement says so**, and is not left blank. Blank reads as "nothing to see".
 * - **No prior year is not a reduction.** A first year has nothing to compare, and printing
 *   −100% would be an assertion about a year nobody measured (ADR-0032's question from the other
 *   side).
 *
 * Marked as EmitIQ's own analysis: the verification log carries nothing on an obligation to explain
 * a year-on-year movement (rule 2), so this is not a disclosure and carries no paragraph.
 */
export function Movement({
  obligationId,
  method,
  entityName,
}: {
  obligationId: string;
  method: Scope2Method;
  /** The filing's company, by the name the register filters on. The bridge's totals are this
   * company's, so a total opens the register for this company and not for the whole workspace. */
  entityName: string;
}) {
  const comparison = useReportComparison(obligationId, method);
  const data = comparison.data;
  /** Whether the list below is narrowed to the residual's sites. The explained and unexplained
   * totals do not narrow this list any more: each opens a register of its own (`TotalOfSites`), so
   * the sites behind a total are the register's rows, with receipts, and not a second list here. */
  const [focus, setFocus] = useState<"residual" | null>(null);
  // **Every cell below is written by `bridgeCells`** (S1-02): one rounding function, applied once to
  // the exact strings the API sends, at the fewest places at which the printed parts add up.
  const cells = data && !data.prior_is_empty ? bridgeCells(data) : undefined;
  const adds = data && !data.prior_is_empty ? statedBasisCells(data) : undefined;

  return (
    <Panel
      title="Against last year"
      sub="EmitIQ's analysis, not a disclosure"
      column="1 / 13"
    >
      {/* Whether this filing's current window is closed (ADR-0094 D2), from the comparison's own
          response: the movement beneath it is the movement of that window. Nothing while the read
          is pending or failed, which is not a claim that the window is open. */}
      <WindowLockBanner lock={data?.window_lock} />
      {/* **Three states, not two.** A failed read used to remove the whole panel — an absence left
          blank, and the worst version of it: a reader who has seen this panel before concludes the
          product stopped comparing, and one who has not never learns that it compares at all. The
          loading state had the same defect in a milder form (`if (!data && !error) return null`),
          so the panel appeared out of nowhere a second after the screen did. Loading is said
          (`Loaded`'s rule), because a blank panel and a panel with nothing to say look identical. */}
      {comparison.error ? (
        // Wrapped so a test and the browser gate can name **this** panel's failure: there are five
        // reads on this screen and `read-failed` is one testid, so an assertion that found any of
        // them would pass while the comparison was the one that vanished.
        <div data-testid="bridge-failed">
          <ReadFailed
            of="The comparison with last year"
            error={comparison.error}
            onRetry={() => void comparison.refetch()}
          />
        </div>
      ) : !data ? (
        <div data-testid="bridge-loading">
          <Empty>Loading…</Empty>
        </div>
      ) : data.prior_is_empty ? (
        <>
          <p className={own.fine} data-testid="no-prior-year">
            <strong>There is nothing to compare this with.</strong> EmitIQ holds no figures for{" "}
            {shortDate(data.prior_start)} – {shortDate(data.prior_end)}. That is not a reduction: it
            is a year this product did not calculate. A figure your company disclosed for it can be
            held as a comparative, shown as asserted rather than calculated.
          </p>
          {/* **The next action, which that sentence described and did not offer** (EXP-29). It
              named a capability — ADR-0032's asserted comparative — and left the reader to find
              the screen that provides it. */}
          <PriorComparatives
            obligationId={obligationId}
            from={data.prior_start}
            to={data.prior_end}
            method={method}
          />
        </>
      ) : (
        <>
          {/* **The two windows, named in words.** The panel's figures are differences between two
              years, and a reader who cannot see which two years is reading a difference they
              cannot check. */}
          <p className={cls(own.bridgeWindows)} data-testid="bridge-windows">
            {shortDate(data.prior_start)} – {shortDate(data.prior_end)}
            <span aria-hidden="true"> → </span>
            <span className="visuallyHidden"> compared with </span>
            {shortDate(data.current_start)} – {shortDate(data.current_end)}
          </p>

          {/* **The bridge, in the order it reconciles**: last year's total, the drivers, the
              remainder, this year's. The two totals and the residual were on `ComparisonOut`
              before this panel read any of them — `current_total_tonnes` and `prior_total_tonnes`
              were added *for this panel*, on this filing's own boundary and summed over the same
              aggregates the movements come from, and `residual_tonnes` names what the listed
              movements do not account for. While nothing read them the panel showed two drivers
              with no ends, and the paragraph below **said the totals did not exist** — so a reader
              was told the product could not state them while the response carried them.

              The parts add up exactly: `(current − prior) = explained + unexplained + residual`,
              which is why the residual is shown rather than rounded away. A bridge whose parts do
              not add up, with nothing naming the remainder, is the shape a reader checks once and
              then stops trusting. */}
          <div className={cls(own.counts)} data-testid="movement-totals">
            <span className={cls(own.count)} data-testid="bridge-prior-total">
              <span className={cls(own.countN, "num")}>
                <TotalInRegister
                  shown={cells?.prior}
                  exact={data.prior_total_tonnes}
                  from={data.prior_start}
                  to={data.prior_end}
                  entityName={entityName}
                  method={method}
                  what="Last year"
                />
              </span>
              <span className={cls(own.countLabel)}>tCO₂e last year, on this boundary</span>
            </span>
            <span className={cls(own.count)}>
              {/* **Signed, and grouped.** The direction is the first thing anybody asks, and an
                  unsigned figure reads as a magnitude — `difference-delta` on the reconciliation
                  screen already carries a sign for that reason. The grouping is `format.ts`,
                  because the wire sends `1629851.40` and every other screen in this product
                  renders that number as `1,629,851.4`: one filed figure with two renderings inside
                  one product is the defect `amount()` above was removed for. */}
              <span
                className={cls(own.countN, "num")}
                data-testid="bridge-explained"
              >
                <TotalOfSites
                  shown={cells?.explained}
                  exact={data.explained_tonnes}
                  data={data}
                  which="explained"
                  obligationId={obligationId}
                  method={method}
                  what="Boundary changes"
                />
              </span>
              <span className={cls(own.countLabel)}>tCO₂e from boundary changes</span>
            </span>
            <span className={cls(own.count)}>
              <span
                className={cls(own.countN, "num")}
                data-testid="bridge-unexplained"
              >
                <TotalOfSites
                  shown={cells?.unexplained}
                  exact={data.unexplained_tonnes}
                  data={data}
                  which="unexplained"
                  obligationId={obligationId}
                  method={method}
                  what="Not explained by a boundary change"
                />
              </span>
              <span className={cls(own.countLabel)}>tCO₂e not explained by one</span>
            </span>
            <span className={cls(own.count)} data-testid="bridge-residual">
              {/* Signed like the drivers beside it, because it is one: it is the part of the same
                  difference that the sites above are too small to carry. */}
              <span className={cls(own.countN, "num")}>
                {/* **The movements too small to list have no list to narrow to.** Each is below the
                    materiality line, so they are in this year's register among every other figure and
                    nowhere on their own; the link says so in its name rather than imply otherwise. */}
                <TotalInRegister
                  shown={cells?.residual}
                  exact={data.residual_tonnes}
                  from={data.current_start}
                  to={data.current_end}
                  entityName={entityName}
                  method={method}
                  what="Movements too small to list"
                  scattered
                />
              </span>
              <span className={cls(own.countLabel)}>
                tCO₂e in movements too small to list
              </span>
              {/* **The residual has a list behind it** (queue item 5b). It is a sum of sites that each
                  moved by less than the listing threshold, and the server now names them
                  (`residual_sites`, whose deltas add up to the residual exactly). A button, not a
                  link: the sites are on this page. The figure above still opens the register. Not
                  offered when the response carried no list or the list is empty. */}
              {data.residual_sites && data.residual_sites.length > 0 ? (
                <button
                  type="button"
                  className={cls(styles.fixLink, own.residualToggle)}
                  aria-pressed={focus === "residual"}
                  data-testid="bridge-residual-toggle"
                  onClick={() => setFocus(focus === "residual" ? null : "residual")}
                >
                  {focus === "residual"
                    ? "Show every site"
                    : `Show the ${data.residual_sites.length} site${data.residual_sites.length === 1 ? "" : "s"} behind this`}
                </button>
              ) : null}
            </span>
            <span className={cls(own.count)} data-testid="bridge-current-total">
              <span className={cls(own.countN, "num")}>
                <TotalInRegister
                  shown={cells?.current}
                  exact={data.current_total_tonnes}
                  from={data.current_start}
                  to={data.current_end}
                  entityName={entityName}
                  method={method}
                  what="This year"
                />
              </span>
              <span className={cls(own.countLabel)}>tCO₂e this year, on this boundary</span>
            </span>
          </div>

          {data.sentence ? (
            <p className={own.notice} data-testid="movement-sentence">
              <span className={own.noticeTitle}>Part of this change is the boundary</span>
              {data.sentence}
            </p>
          ) : null}

          {/* **What these totals are, said because the obvious reading of them is wrong.** They
              are EmitIQ's own sums over this filing's boundary — the basis the drivers are on, so
              the arithmetic holds — and they are **not** the filed figure: a report's
              `stated_total_tonnes` leaves SECR's contested guidance line out (B-19), and
              `/inventory` answers for the whole workspace rather than for this filing. Three
              bases, and a panel that mixed them would put an organisation-wide number beside
              boundary-clamped drivers and call the difference a trend.

              This paragraph used to say the product *could not state* last year's total on this
              boundary. It could, and did, on the same response. */}
          <p className={cls(own.bridgeScope)} data-testid="bridge-scope">
            The sites listed are those whose contribution moved by more than a tenth of a tonne;
            everything smaller is in the residual, so the four figures above account for the whole
            of the difference between the two years. Both totals are this filing&rsquo;s own
            boundary.
          </p>
          {/* **Where the bridge's total and the filing's stated total differ, the difference is
              named line by line** (Q-22, G-P7-07). This paragraph used to say the two were "not the
              same basis" and stop there: SECR's bridge read 1,306.1 t beside a stated 1,262.2 t and
              a reader had to guess at the 43.9 t between them. It is the guidance-only line the
              report prints without adding, plus the emissions no line of the report sums at all.
              Silent when the two are the same number, which is SB 253. */}
          {adds ? (
            <p className={cls(own.bridgeScope)} data-testid="bridge-adds">
              This year&rsquo;s total is the filing&rsquo;s stated total,{" "}
              <strong className="num">{adds.stated}</strong> tCO₂e, plus{" "}
              {adds.parts
                .map((part) =>
                  part.kind === "guidance_line"
                    ? `${part.shown} for ${part.label} (printed on the report, not added to its total)`
                    : `${part.shown} of ${categoryLabel(part.key)} (no line of this report covers it)`,
                )
                .concat(
                  adds.unlisted === null
                    ? []
                    : [`${adds.unlisted} that no line of this report accounts for`],
                )
                .join("; ")}
              .
            </p>
          ) : null}

          {cells && cells.places > BASE_PLACES ? (
            <p className={cls(own.bridgeScope)} data-testid="bridge-precision">
              These figures are shown to {cells.places} decimal places because at one decimal the
              parts would not add up to the totals. Nothing is adjusted to make them foot: a figure
              is rounded once from its exact value, and a group that would disagree is shown more
              precisely instead.
            </p>
          ) : null}

          {focus ? (
            <p className={cls(own.bridgeScope)} data-testid="bridge-focus">
              Showing only the sites behind the movements too small to list.{" "}
              <button type="button" className={styles.fixLink} onClick={() => setFocus(null)}>
                Show every site
              </button>
            </p>
          ) : null}

          {focus === "residual" && data.residual_sites ? (
            <ResidualSites
              sites={data.residual_sites}
              cells={cells?.residualRows ?? []}
              data={data}
              method={method}
            />
          ) : data.movements.length === 0 ? (
            <Empty>
              Nothing moved materially against {shortDate(data.prior_start)} –{" "}
              {shortDate(data.prior_end)}.
            </Empty>
          ) : (
            <ul className={cls(own.movements)}>
              {data.movements.map((movement, index) => {
                const row = cells?.rows[index];
                return (
                <li key={movement.facility_id} className={cls(own.movement)}>
                  <span className={cls(own.movementSite)}>{movement.facility_name}</span>
                  <span className={cls(own.movementFigures, "num")} data-testid="movement-figures">
                    {/* **Each end of the movement opens its own figures** (design rule 2). A
                        difference a reader cannot take apart is one they have to take on trust,
                        and the register for *that* window is where every figure behind it has a
                        receipt. The window on each link is the window of the figure it labels: one
                        link to the current year beside last year's number would be the
                        stale-figure failure in its quietest form. */}
                    <FigureInWindow
                      tonnes={movement.prior_tonnes}
                      places={row?.places ?? BASE_PLACES}
                      site={movement.facility_id}
                      siteName={movement.facility_name}
                      from={data.prior_start}
                      to={data.prior_end}
                      method={method}
                    />
                    <span aria-hidden="true"> → </span>
                    <span className="visuallyHidden"> became </span>
                    <FigureInWindow
                      tonnes={movement.current_tonnes}
                      places={row?.places ?? BASE_PLACES}
                      site={movement.facility_id}
                      siteName={movement.facility_name}
                      from={data.current_start}
                      to={data.current_end}
                      method={method}
                    />
                    {" tCO₂e · "}
                    <span data-testid="movement-delta">{row?.delta}</span>
                  </span>
                  <span
                    className={cls(
                      own.movementWhy,
                      movement.because ? undefined : own.movementUnexplained,
                    )}
                  >
                    {/* **Never blank.** An unexplained movement is a real finding — it may be a
                        reduction, a meter that stopped being read, or a bill nobody uploaded, and
                        the product does not know which. Saying so is the honest version. */}
                    {movement.because ??
                      "Not explained by a boundary change. This may be a real reduction, or a reading nobody recorded this year — open the site in the register."}
                  </span>
                </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Panel>
  );
}


/** The sites the residual is made of: each moved by less than the listing threshold, so none has a
 * sentence of its own, and together they are exactly the residual. Each end opens its own window's
 * figures, like a listed movement; a movement here is never called "unexplained", because that word
 * is for a movement large enough to have been looked at. */
function ResidualSites({
  sites,
  cells,
  data,
  method,
}: {
  sites: ReadonlyArray<{
    facility_id: string;
    facility_name: string;
    prior_tonnes: string;
    current_tonnes: string;
  }>;
  cells: ReadonlyArray<{ places: number; delta: string }>;
  data: { prior_start: string; prior_end: string; current_start: string; current_end: string };
  method: Scope2Method;
}) {
  return (
    <ul className={cls(own.movements)} data-testid="residual-sites">
      {sites.map((site, index) => {
        const row = cells[index];
        return (
          <li key={site.facility_id} className={cls(own.movement)}>
            <span className={cls(own.movementSite)}>{site.facility_name}</span>
            <span className={cls(own.movementFigures, "num")}>
              <FigureInWindow
                tonnes={site.prior_tonnes}
                places={row?.places ?? BASE_PLACES}
                site={site.facility_id}
                siteName={site.facility_name}
                from={data.prior_start}
                to={data.prior_end}
                method={method}
              />
              <span aria-hidden="true"> → </span>
              <span className="visuallyHidden"> became </span>
              <FigureInWindow
                tonnes={site.current_tonnes}
                places={row?.places ?? BASE_PLACES}
                site={site.facility_id}
                siteName={site.facility_name}
                from={data.current_start}
                to={data.current_end}
                method={method}
              />
              {" tCO₂e · "}
              <span data-testid="residual-delta">{row?.delta}</span>
            </span>
            <span className={cls(own.movementWhy)}>
              Moved by less than a tenth of a tonne, so it is in the residual and has no sentence of
              its own.
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** A bridge total that is a sum over a whole window, and the register for exactly that window.
 *
 * **The register for the window the total is for** (rule 3, finding 8): the five totals on this
 * panel were plain text, so the largest numbers on the screen had no way to the figures behind
 * them while the small ones beside them did. The link carries this company (the total is on this
 * filing's boundary) and the window the total belongs to; last year's total opens last year's.
 *
 * A zero is printed and is not a link: the register has nothing to show for it, and (as for
 * `FigureInWindow`) a link to an empty window would land on a screen that looks like a mistake.
 * `scattered` says in the link's name that the figure is a sum of pieces that have no list of their
 * own, so a reader is not told the register shows only them.
 */
function TotalInRegister({
  shown,
  exact,
  from,
  to,
  entityName,
  method,
  what,
  scattered = false,
}: {
  shown: string | undefined;
  exact: string;
  from: string;
  to: string;
  entityName: string;
  method: Scope2Method;
  what: string;
  scattered?: boolean;
}) {
  if (shown === undefined || Number(exact) === 0) return <>{shown}</>;
  return (
    <Link
      to="/emissions"
      search={{ method, from, to, entity: entityName, tab: "register" }}
      aria-label={`${what}: ${shown} tonnes. Open the figures for ${entityName}, ${shortDate(from)} to ${shortDate(to)}${scattered ? ", which these movements are part of" : ""}`}
    >
      {shown}
    </Link>
  );
}

/** A bridge total that is the sum of some sites' movements, opening a register of exactly those
 * sites.
 *
 * **The sites are derived from the bridge's own movements** (`sitesBehind`), never copied into a
 * second list: the boundary-change total is the movements a boundary change explains and the
 * unexplained one the rest, the same `because` that sorted them into the total. The link carries
 * those facility ids, the filing (so the register can say what they explain, from that filing's own
 * comparison) and the window the sites' figures are in, which is last year for a site that was sold.
 *
 * A zero is printed and is not a link, and neither is a total with no listed site behind it: the
 * register would have nothing to narrow to, and a link to a screen that looks like a mistake is
 * worse than a number that says it has nothing behind it.
 */
function TotalOfSites({
  shown,
  exact,
  data,
  which,
  obligationId,
  method,
  what,
}: {
  shown: string | undefined;
  exact: string;
  data: {
    movements: ReadonlyArray<{
      facility_id: string;
      facility_name: string;
      because?: string | null;
      prior_tonnes: string;
      current_tonnes: string;
      delta_tonnes: string;
    }>;
    prior_start: string;
    prior_end: string;
    current_start: string;
    current_end: string;
  };
  which: SitesTotal;
  obligationId: string;
  method: Scope2Method;
  what: string;
}) {
  const behind = sitesBehind(data, which);
  if (shown === undefined || Number(exact) === 0 || behind.ids.length === 0) return <>{shown}</>;
  const from = behind.window === "prior" ? data.prior_start : data.current_start;
  const to = behind.window === "prior" ? data.prior_end : data.current_end;
  return (
    <Link
      to="/emissions"
      search={{
        method,
        from,
        to,
        sites: behind.ids.join(","),
        filing: obligationId,
        tab: "register",
      }}
      aria-label={`${what}: ${shown} tonnes. Open the figures for the ${behind.ids.length} site${behind.ids.length === 1 ? "" : "s"} behind this, ${shortDate(from)} to ${shortDate(to)}`}
    >
      {shown}
    </Link>
  );
}


/** One end of a movement: the figure, and the register for the window it belongs to.
 *
 * **A zero is not a link, and that is the whole of this component.** The register drops a site
 * filter it could not have offered (`register.ts`'s `effective`), and a site with no figures in a
 * window is not on offer there — so a link to last year for a site acquired this year would land
 * on the **whole** register with the filter silently gone, which is a reader sent to look at one
 * site and shown every site, with nothing saying so. The figure is still printed, because a zero
 * contribution is an answer.
 */
export function FigureInWindow({
  tonnes: value,
  places = BASE_PLACES,
  site,
  siteName,
  from,
  to,
  method,
}: {
  tonnes: string;
  /** Decimal places this figure is printed to - the group's, from `bridgeCells`. */
  places?: number;
  site: string;
  siteName: string;
  from: string;
  to: string;
  method: Scope2Method;
}) {
  const shown = quantity(value, places);
  if (Number(value) === 0) return <>{shown}</>;
  return (
    <Link
      to="/emissions"
      search={{ method, from, to, site, tab: "register" }}
      aria-label={`${siteName}, ${shown} tonnes for ${shortDate(from)} to ${shortDate(to)} — open these figures in the register`}
    >
      {shown}
    </Link>
  );
}


/** The first-year branch's next action — **ADR-0032**, and UX-01's blocker read again.
 *
 * The coverage row for UX-01 recorded the comparison bridge as blocked on ADR-0032 while it was
 * *Proposed*, because *"an imported prior-year total has none"* of a receipt. It was accepted on
 * 2026-10-04 (option 3 where the activity data exists, option 2 where it does not, never option 1)
 * and built, so an asserted comparative now resolves to **rule 3's second species** of receipt and
 * the objection no longer holds.
 *
 * **What this does not do is render the figure.** `/prior-disclosures` is where a disclosed figure,
 * its provenance and its receipt are rendered, and a second rendering here would be a second copy
 * of a number — the defect the record page's "borrow, never re-render" rule exists to prevent, on
 * the one kind of figure this product must never show as calculated. So this says how many are
 * held and sends the reader to the screen that owns them.
 *
 * **A failed read is not "none held".** The count is a claim about the company's own filing
 * history, and zero is the answer that stops somebody looking.
 */
export function PriorComparatives({
  obligationId,
  from,
  to,
  method,
}: {
  obligationId: string;
  from: string;
  to: string;
  method: Scope2Method;
}) {
  const held = useImportedFigures(obligationId);
  const forThatYear = (held.data ?? []).filter(
    (figure) =>
      figure.receipt.period_start === from &&
      figure.receipt.period_end === to &&
      !figure.receipt.superseded,
  );

  return (
    <p className={cls(own.bridgeNext)} data-testid="prior-comparatives">
      {held.error ? (
        <>
          What your company has already disclosed for that year could not be read, so this does not
          say whether a comparative is held.{" "}
          <button type="button" className={styles.fixLink} onClick={() => void held.refetch()}>
            Try again
          </button>
        </>
      ) : held.isPending ? (
        "Checking what your company has already disclosed for that year…"
      ) : forThatYear.length === 0 ? (
        <>
          Nothing is held for that year yet.{" "}
          <Link to="/prior-disclosures" search={{ method, filing: obligationId }}>
            Record what your company disclosed
          </Link>{" "}
          — it is held as asserted, never calculated, and never added to this year&rsquo;s
          inventory.
        </>
      ) : (
        <>
          {forThatYear.length} figure{forThatYear.length === 1 ? "" : "s"} your company disclosed
          for that year {forThatYear.length === 1 ? "is" : "are"} held, asserted rather than
          calculated.{" "}
          <Link to="/prior-disclosures" search={{ method, filing: obligationId }}>
            Open the reconciliation
          </Link>
          .
        </>
      )}
    </p>
  );
}
